import React, { useState, useEffect, useRef } from 'react';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import { RefreshCw, User, Truck, MapPin, Phone, Clock, AlertTriangle, X } from 'lucide-react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

// Fix for default marker icons in React
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

// Custom driver icon
const createDriverIcon = (isActive) => {
  return L.divIcon({
    className: 'custom-driver-marker',
    html: `
      <div style="
        width: 40px;
        height: 40px;
        border-radius: 50%;
        background-color: ${isActive ? '#10B981' : '#6B7280'};
        border: 3px solid white;
        box-shadow: 0 2px 8px rgba(0,0,0,0.3);
        display: flex;
        align-items: center;
        justify-content: center;
        color: white;
        font-weight: bold;
        font-size: 18px;
      ">
        🚗
      </div>
    `,
    iconSize: [40, 40],
    iconAnchor: [20, 20],
    popupAnchor: [0, -20]
  });
};

// Component to handle map updates
const MapUpdater = ({ center, zoom }) => {
  const map = useMap();
  useEffect(() => {
    map.setView(center, zoom);
  }, [map, center, zoom]);
  return null;
};

const DriverTracking = () => {
  const [loading, setLoading] = useState(false);
  const [selectedDriver, setSelectedDriver] = useState(null);
  const [mapCenter, setMapCenter] = useState([25.2048, 55.2708]); // Dubai coordinates
  const [mapZoom, setMapZoom] = useState(12);
  const [mapReady, setMapReady] = useState(false);
  const mapRef = useRef(null);

  // Dummy driver data with positions
  const [drivers, setDrivers] = useState([
    {
      Id: 1,
      Name: 'Ahmed Ali',
      DriverId: 'DRV001',
      VehicleNumber: 'ABC-1234',
      Phone: '+971 50 123 4567',
      Status: 'Active',
      IsActive: true,
      CurrentLocation: {
        lat: 25.2048,
        lng: 55.2708
      },
      LastUpdate: '2024-01-15 10:30 AM',
      TotalDeliveries: 45,
      TodayDeliveries: 12,
      OnTimeRate: '95%'
    },
    {
      Id: 2,
      Name: 'Mohammed Hassan',
      DriverId: 'DRV002',
      VehicleNumber: 'XYZ-5678',
      Phone: '+971 50 234 5678',
      Status: 'Active',
      IsActive: true,
      CurrentLocation: {
        lat: 25.2148,
        lng: 55.2808
      },
      LastUpdate: '2024-01-15 10:28 AM',
      TotalDeliveries: 38,
      TodayDeliveries: 8,
      OnTimeRate: '92%'
    },
    {
      Id: 3,
      Name: 'Omar Ibrahim',
      DriverId: 'DRV003',
      VehicleNumber: 'DEF-9012',
      Phone: '+971 50 345 6789',
      Status: 'Inactive',
      IsActive: false,
      CurrentLocation: {
        lat: 25.1948,
        lng: 55.2608
      },
      LastUpdate: '2024-01-15 09:45 AM',
      TotalDeliveries: 52,
      TodayDeliveries: 15,
      OnTimeRate: '98%'
    },
    {
      Id: 4,
      Name: 'Khalid Al Maktoum',
      DriverId: 'DRV004',
      VehicleNumber: 'GHI-3456',
      Phone: '+971 50 456 7890',
      Status: 'Active',
      IsActive: true,
      CurrentLocation: {
        lat: 25.2248,
        lng: 55.2908
      },
      LastUpdate: '2024-01-15 10:32 AM',
      TotalDeliveries: 67,
      TodayDeliveries: 18,
      OnTimeRate: '94%'
    },
    {
      Id: 5,
      Name: 'Saeed Al Zahra',
      DriverId: 'DRV005',
      VehicleNumber: 'JKL-7890',
      Phone: '+971 50 567 8901',
      Status: 'Active',
      IsActive: true,
      CurrentLocation: {
        lat: 25.1848,
        lng: 55.2508
      },
      LastUpdate: '2024-01-15 10:29 AM',
      TotalDeliveries: 41,
      TodayDeliveries: 10,
      OnTimeRate: '91%'
    }
  ]);

  const handleRefresh = async () => {
    setLoading(true);
    // TODO: Replace with actual API call when available
    // const response = await MainService.GetDriverLocations();
    // if (response) {
    //   setDrivers(response);
    // }
    setTimeout(() => {
      setLoading(false);
    }, 1000);
  };

  const handleMarkerClick = (driver) => {
    setSelectedDriver(driver);
  };

  const handleCloseSummary = () => {
    setSelectedDriver(null);
  };

  useEffect(() => {
    // Auto-refresh driver locations every 30 seconds
    const interval = setInterval(() => {
      handleRefresh();
    }, 30000);

    return () => clearInterval(interval);
  }, []);

  // Force map to invalidate size after mount
  useEffect(() => {
    if (mapRef.current) {
      setTimeout(() => {
        mapRef.current.invalidateSize();
      }, 100);
    }
  }, [mapReady]);

  return (
    <div className="flex flex-col bg-gray-100" style={{ margin: '-24px', height: 'calc((100vh - 64px) * 0.9)', overflow: 'hidden' }}>
      {/* Header */}
      <div className="bg-white border-b border-gray-200 p-4 shadow-sm flex-shrink-0">
        <div className="flex items-center justify-between">
          <div className="flex items-center">
            <MapPin className="w-6 h-6 text-primary mr-2" />
            <h1 className="text-2xl font-bold text-gray-900">Driver Tracking</h1>
          </div>
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full bg-green-500"></div>
                <span className="text-sm text-gray-600">Active</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full bg-gray-500"></div>
                <span className="text-sm text-gray-600">Inactive</span>
              </div>
            </div>
            <button
              onClick={handleRefresh}
              disabled={loading}
              className="flex items-center gap-2 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              <span>Refresh</span>
            </button>
          </div>
        </div>
        <p className="text-sm text-gray-500 mt-2 ml-8">
          Real-time tracking of driver locations on the map
        </p>
      </div>

      {/* Map Container */}
      <div 
        className="flex-1 relative" 
        style={{ 
          height: '100%',
          width: '100%',
          flex: '1 1 auto'
        }}
        id="map-container"
      >
        <MapContainer
          key="driver-tracking-map"
          center={mapCenter}
          zoom={mapZoom}
          style={{ height: '100%', width: '100%', zIndex: 1 }}
          scrollWheelZoom={true}
          whenReady={(map) => {
            mapRef.current = map.target;
            setMapReady(true);
            setTimeout(() => {
              map.target.invalidateSize();
            }, 200);
          }}
        >
          <MapUpdater center={mapCenter} zoom={mapZoom} />
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            maxZoom={19}
            updateWhenZooming={false}
            updateWhenIdle={true}
          />
          {drivers.map((driver) => (
            <Marker
              key={driver.Id}
              position={[driver.CurrentLocation.lat, driver.CurrentLocation.lng]}
              icon={createDriverIcon(driver.IsActive)}
              eventHandlers={{
                click: () => handleMarkerClick(driver)
              }}
            >
              <Popup>
                <div className="text-sm">
                  <p className="font-semibold">{driver.Name}</p>
                  <p className="text-gray-600">{driver.VehicleNumber}</p>
                  <p className={`text-xs ${driver.IsActive ? 'text-green-600' : 'text-gray-500'}`}>
                    {driver.Status}
                  </p>
                </div>
              </Popup>
            </Marker>
          ))}
        </MapContainer>

        {/* Driver Summary Panel */}
        {selectedDriver && (
          <div className="absolute top-4 right-4 bg-white rounded-lg shadow-xl border border-gray-200 w-80 z-50 max-h-[calc(100vh-120px)] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-gray-200 p-4 flex items-center justify-between">
              <h2 className="text-lg font-bold text-gray-900">Driver Summary</h2>
              <button
                onClick={handleCloseSummary}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-4 space-y-4">
              {/* Driver Info */}
              <div className="space-y-3">
                <div className="flex items-center gap-3 pb-3 border-b border-gray-200">
                  <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
                    selectedDriver.IsActive ? 'bg-green-100' : 'bg-gray-100'
                  }`}>
                    <User className={`w-6 h-6 ${
                      selectedDriver.IsActive ? 'text-green-600' : 'text-gray-600'
                    }`} />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900">{selectedDriver.Name}</h3>
                    <p className="text-sm text-gray-500">ID: {selectedDriver.DriverId}</p>
                  </div>
                </div>

                {/* Vehicle Info */}
                <div className="flex items-center gap-3">
                  <Truck className="w-5 h-5 text-gray-400" />
                  <div>
                    <p className="text-sm font-medium text-gray-900">Vehicle</p>
                    <p className="text-sm text-gray-600">{selectedDriver.VehicleNumber}</p>
                  </div>
                </div>

                {/* Phone */}
                <div className="flex items-center gap-3">
                  <Phone className="w-5 h-5 text-gray-400" />
                  <div>
                    <p className="text-sm font-medium text-gray-900">Phone</p>
                    <p className="text-sm text-gray-600">{selectedDriver.Phone}</p>
                  </div>
                </div>

                {/* Status */}
                <div className="flex items-center gap-3">
                  <div className={`w-3 h-3 rounded-full ${selectedDriver.IsActive ? 'bg-green-500' : 'bg-gray-500'}`}></div>
                  <div>
                    <p className="text-sm font-medium text-gray-900">Status</p>
                    <p className={`text-sm ${selectedDriver.IsActive ? 'text-green-600' : 'text-gray-600'}`}>
                      {selectedDriver.Status}
                    </p>
                  </div>
                </div>

                {/* Last Update */}
                <div className="flex items-center gap-3">
                  <Clock className="w-5 h-5 text-gray-400" />
                  <div>
                    <p className="text-sm font-medium text-gray-900">Last Update</p>
                    <p className="text-sm text-gray-600">{selectedDriver.LastUpdate}</p>
                  </div>
                </div>
              </div>

              {/* Statistics */}
              <div className="pt-4 border-t border-gray-200">
                <h4 className="text-sm font-semibold text-gray-900 mb-3">Statistics</h4>
                <div className="grid grid-cols-2 gap-3">
                  <div className="bg-gray-50 rounded-lg p-3">
                    <p className="text-xs text-gray-500">Total Deliveries</p>
                    <p className="text-lg font-bold text-gray-900">{selectedDriver.TotalDeliveries}</p>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-3">
                    <p className="text-xs text-gray-500">Today Deliveries</p>
                    <p className="text-lg font-bold text-gray-900">{selectedDriver.TodayDeliveries}</p>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-3 col-span-2">
                    <p className="text-xs text-gray-500">On-Time Rate</p>
                    <p className="text-lg font-bold text-green-600">{selectedDriver.OnTimeRate}</p>
                  </div>
                </div>
              </div>

              {/* Location Info */}
              <div className="pt-4 border-t border-gray-200">
                <h4 className="text-sm font-semibold text-gray-900 mb-2">Current Location</h4>
                <p className="text-xs text-gray-600">
                  Lat: {selectedDriver.CurrentLocation.lat.toFixed(6)}
                </p>
                <p className="text-xs text-gray-600">
                  Lng: {selectedDriver.CurrentLocation.lng.toFixed(6)}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Driver List Panel (Collapsible) */}
        <div className="absolute top-4 left-4 bg-white rounded-lg shadow-xl border border-gray-200 w-64 z-50 max-h-[calc(100vh-120px)] overflow-y-auto">
          <div className="p-4 border-b border-gray-200">
            <h3 className="font-semibold text-gray-900">Drivers ({drivers.length})</h3>
          </div>
          <div className="divide-y divide-gray-200">
            {drivers.map((driver) => (
              <button
                key={driver.Id}
                onClick={() => {
                  setSelectedDriver(driver);
                  setMapCenter([driver.CurrentLocation.lat, driver.CurrentLocation.lng]);
                  setMapZoom(14);
                }}
                className={`w-full p-3 text-left hover:bg-gray-50 transition-colors ${
                  selectedDriver?.Id === driver.Id ? 'bg-green-50 border-l-4 border-green-500' : ''
                }`}
              >
                <div className="flex items-center gap-3">
                  <div className={`w-3 h-3 rounded-full ${driver.IsActive ? 'bg-green-500' : 'bg-gray-500'}`}></div>
                  <div className="flex-1">
                    <p className="text-sm font-medium text-gray-900">{driver.Name}</p>
                    <p className="text-xs text-gray-500">{driver.VehicleNumber}</p>
                  </div>
                </div>
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default DriverTracking;

